import { Controller } from "stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// #1732: het bord van Sales & Leads. Kaarten slepen tussen kolommen (native
// drag and drop), daarna een PATCH naar verplaats; het antwoord is een Turbo
// Stream die het bord en eventueel een melding opnieuw tekent. Een kaart die
// automatisch in een weekkolom staat, kan niet naar een andere week: de
// server weigert dat en legt uit wat er moet gebeuren (startdatum, pauze of
// trajectinstelling aanpassen). Het bord geeft dat al aan tijdens het slepen.
export default class extends Controller {
  static targets = ["kolom", "bord"]
  static values = { filters: String, bordUrl: String }

  start(event) {
    const kaart = event.currentTarget
    this.gesleept = kaart
    this.bronKolom = kaart.closest("[data-kolom-id]")
    kaart.classList.add("is-sleept")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", kaart.dataset.id)
  }

  einde() {
    if (this.gesleept) this.gesleept.classList.remove("is-sleept")
    this.kolomTargets.forEach((kolom) => kolom.classList.remove("is-doel", "is-geweigerd"))
    this.gesleept = null
  }

  over(event) {
    if (!this.gesleept) return
    event.preventDefault()
    const kolom = event.currentTarget
    const geweigerd = this.geweigerd(kolom)
    kolom.classList.toggle("is-doel", !geweigerd)
    kolom.classList.toggle("is-geweigerd", geweigerd)
    // Ook bij een geweigerde kolom mag de kaart losgelaten worden: de server
    // laat hem staan en stuurt de uitleg als melding mee.
    event.dataTransfer.dropEffect = "move"
    if (geweigerd) return

    // #1768: een kolom op programmaweek zet de kaarten zelf op volgorde.
    // Binnen die kolom schuift er niets; van elders komt de kaart onderaan
    // tot het bord opnieuw getekend is en hij op zijn week staat.
    if (kolom.dataset.gesorteerd === "true") {
      if (kolom !== this.gesleept.parentElement) kolom.appendChild(this.gesleept)
      return
    }

    const erna = this.kaartNa(kolom, event.clientY)
    if (erna) kolom.insertBefore(this.gesleept, erna)
    else kolom.appendChild(this.gesleept)
  }

  weg(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      event.currentTarget.classList.remove("is-doel", "is-geweigerd")
    }
  }

  neer(event) {
    event.preventDefault()
    const kaart = this.gesleept
    if (!kaart) return

    const kolom = event.currentTarget
    const kaarten = Array.from(kolom.querySelectorAll(".sl-kaart"))
    const positie = Math.max(kaarten.indexOf(kaart), 0)
    this.einde()
    this.verplaats(kaart, kolom.dataset.kolomId, positie)
  }

  // Een automatisch ingedeelde kaart uit een automatische kolom naar een
  // andere automatische kolom: dat bepaalt de startdatum, niet het slepen.
  // De server controleert hetzelfde en geeft de uitleg.
  geweigerd(kolom) {
    if (!this.gesleept || !this.bronKolom) return false
    if (kolom === this.bronKolom) return false
    return this.gesleept.dataset.automatisch === "true" &&
      this.bronKolom.dataset.automatisch === "true" &&
      kolom.dataset.automatisch === "true"
  }

  kaartNa(kolom, y) {
    const kaarten = Array.from(kolom.querySelectorAll(".sl-kaart:not(.is-sleept)"))
    return kaarten.find((kaart) => {
      const rect = kaart.getBoundingClientRect()
      return y < rect.top + rect.height / 2
    })
  }

  verplaats(kaart, kolomId, positie) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams(this.filtersValue || "")
    body.set("kolom_id", kolomId)
    body.set("positie", positie)
    fetch(kaart.dataset.url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token,
        "Accept": "text/vnd.turbo-stream.html",
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: body.toString(),
      credentials: "same-origin"
    }).then((antwoord) => {
      if (!antwoord.ok) throw new Error(antwoord.status)
      return antwoord.text()
    }).then((html) => {
      Turbo.renderStreamMessage(html)
    }).catch(() => {
      window.location.reload()
    })
  }

  // Het driepuntjesmenu: er staat er maar één tegelijk open
  menu(event) {
    if (!event.currentTarget.open) return
    this.element.querySelectorAll(".sl-menu[open]").forEach((menu) => {
      if (menu !== event.currentTarget) menu.removeAttribute("open")
    })
  }

  kopieerLink(event) {
    event.preventDefault()
    const knop = event.currentTarget
    const link = knop.dataset.link
    const klaar = () => {
      const tekst = knop.textContent
      knop.textContent = knop.closest("[data-gekopieerd]")?.dataset.gekopieerd || "✓"
      setTimeout(() => { knop.textContent = tekst }, 1500)
    }
    if (navigator.clipboard) navigator.clipboard.writeText(link).then(klaar).catch(() => window.prompt("", link))
    else window.prompt("", link)
  }

  connect() {
    // Een veld dat nog bezig is met opslaan (sl-autosave) mag niet verloren gaan
    // als het venster sluit: dan sluit het pas zodra het antwoord binnen is.
    this.naStream = (event) => {
      if (!this.sluitNaOpslaan || event.target.getAttribute("target") !== "sl_paneel") return
      const origineel = event.detail.render
      event.detail.render = async (element) => {
        await origineel(element)
        this.leegPaneel()
      }
    }
    document.addEventListener("turbo:before-stream-render", this.naStream)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.naStream)
    clearTimeout(this.sluitTimer)
  }

  // #1732 vervolg: het venster sluiten zonder de pagina te herladen (kruisje,
  // klik op de achtergrond of Escape); de scrollpositie van het bord blijft.
  sluitPaneel(event) {
    const frame = document.getElementById("sl_paneel")
    if (!frame) return
    event?.preventDefault()
    const actief = document.activeElement
    if (actief && frame.contains(actief) && actief.form) actief.blur()
    if (frame.querySelector("[data-sl-bezig]")) {
      this.sluitNaOpslaan = true
      frame.classList.add("is-sluitend")
      this.sluitTimer = setTimeout(() => this.leegPaneel(), 4000)
      return
    }
    this.leegPaneel()
  }

  leegPaneel() {
    clearTimeout(this.sluitTimer)
    this.sluitNaOpslaan = false
    const frame = document.getElementById("sl_paneel")
    if (!frame) return
    frame.removeAttribute("src")
    frame.innerHTML = ""
    frame.classList.remove("is-sluitend")
    this.element.querySelectorAll(".sl-kaart.is-gekozen").forEach((kaart) => kaart.classList.remove("is-gekozen"))
    if (this.bordUrlValue) window.history.replaceState(window.history.state, "", this.bordUrlValue)
  }

  // Escape sluit eerst een open menu in het venster, daarna het venster zelf.
  // Een geopende dialoog (nieuwe kaart) sluit de browser zelf.
  toets(event) {
    const frame = document.getElementById("sl_paneel")
    if (!frame || !frame.querySelector(".sl-paneel")) return
    if (document.querySelector("dialog[open]")) return
    const menu = frame.querySelector(".sl-menu[open]")
    if (menu) {
      menu.removeAttribute("open")
      return
    }
    this.sluitPaneel(event)
  }

  // Na het openen krijgt het venster de focus, zodat Escape en Tab erin werken
  paneelGeladen(event) {
    if (event.target.id !== "sl_paneel") return
    const paneel = event.target.querySelector(".sl-paneel")
    if (paneel && !paneel.contains(document.activeElement)) paneel.focus({ preventScroll: true })
  }
}
